<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item label="展示文案" prop="name">
        <el-input v-model="formData.name" maxlength="32" placeholder="如：正品保障" />
      </el-form-item>
      <el-form-item label="图标" prop="iconUrl">
        <UploadImg v-model="formData.iconUrl" height="80px" />
        <div class="text-gray-400 text-xs mt-1">可不传，C 端将使用本地默认图标</div>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="formData.status">
          <el-radio
            v-for="dict in getIntDictOptions(DICT_TYPE.COMMON_STATUS)"
            :key="dict.value"
            :value="dict.value"
          >
            {{ dict.label }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="排序" prop="sort">
        <el-input-number v-model="formData.sort" :min="0" controls-position="right" />
        <span class="text-gray-400 text-xs ml-2">越小越靠前</span>
      </el-form-item>
      <el-alert
        type="info"
        :closable="false"
        title="仅作商详信任背书展示，不代表已对接保司或物流时效。"
        class="mb-2"
      />
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import * as ServiceGuaranteeApi from '@/api/mall/product/serviceGuarantee'
import { CommonStatusEnum } from '@/utils/constants'

defineOptions({ name: 'ProductServiceGuaranteeForm' })

const { t } = useI18n()
const message = useMessage()

const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref('')
const formData = ref({
  id: undefined as number | undefined,
  name: undefined as string | undefined,
  iconUrl: undefined as string | undefined,
  status: CommonStatusEnum.ENABLE as number,
  sort: 0
})
const formRules = reactive({
  name: [{ required: true, message: '展示文案不能为空', trigger: 'blur' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'change' }],
  sort: [{ required: true, message: '排序不能为空', trigger: 'blur' }]
})
const formRef = ref()

const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  if (id) {
    formLoading.value = true
    try {
      formData.value = await ServiceGuaranteeApi.getServiceGuarantee(id)
    } finally {
      formLoading.value = false
    }
  }
}
defineExpose({ open })

const emit = defineEmits(['success'])
const submitForm = async () => {
  if (!formRef.value) return
  const valid = await formRef.value.validate()
  if (!valid) return
  formLoading.value = true
  try {
    const data = formData.value as ServiceGuaranteeApi.ProductServiceGuaranteeVO
    if (formType.value === 'create') {
      await ServiceGuaranteeApi.createServiceGuarantee(data)
      message.success(t('common.createSuccess'))
    } else {
      await ServiceGuaranteeApi.updateServiceGuarantee(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

const resetForm = () => {
  formData.value = {
    id: undefined,
    name: undefined,
    iconUrl: undefined,
    status: CommonStatusEnum.ENABLE,
    sort: 0
  }
  formRef.value?.resetFields()
}
</script>
